import { shallowEqual, useSelector } from 'react-redux';

import type { AppState } from 'src/types/appState';

const caseInsensitiveEquals = (toCompare: string, stringValue?: string) => {
  if (!stringValue) {
    return false;
  }

  return stringValue.toLowerCase() === toCompare.toLowerCase();
};

export const shouldShowPrivateAdToggle = (
  isHirerAccountPrivate: AppState['session']['hirerAccount']['isPrivateAdvertiser'],
  isHirerNationANZorHK: boolean,
) =>
  [!isHirerAccountPrivate, !isHirerNationANZorHK].every((val) => val === true);

export const shouldShowPhoneNumber = (
  isConfidential: AppState['formData']['isConfidential'],
  jobLocationCountry: AppState['formData']['jobLocationCountry'],
) => isConfidential && caseInsensitiveEquals('hk', jobLocationCountry);

export const privateAdFeatureSelector = ({
  formData: { isConfidential, phoneNumber, flowType },
  session: {
    jobLocationCountry,
    hirerAccount: { nation, isPrivateAdvertiser },
  },
}: AppState) => ({
  isConfidential,
  jobLocationCountry,
  phoneNumber,
  flowType,
  hirerNation: nation,
  isHirerAccountPrivate: isPrivateAdvertiser,
});

export const usePrivateAdFeature = (): {
  showPrivateAdToggle: boolean;
  showPhoneNumber: boolean;
} => {
  const {
    flowType,
    hirerNation,
    isHirerAccountPrivate,
    isConfidential,
    jobLocationCountry,
  } = useSelector(privateAdFeatureSelector, shallowEqual);

  const isHirerNationANZ =
    caseInsensitiveEquals('AU', hirerNation) ||
    caseInsensitiveEquals('NZ', hirerNation);

  const isHirerNationANZorHK =
    isHirerNationANZ || caseInsensitiveEquals('HK', hirerNation);

  const showPrivateAdToggle = shouldShowPrivateAdToggle(
    isHirerAccountPrivate,
    flowType === 'edit' ? isHirerNationANZorHK : isHirerNationANZ,
  );

  const showPhoneNumber = shouldShowPhoneNumber(
    isConfidential,
    jobLocationCountry,
  );

  return { showPrivateAdToggle, showPhoneNumber };
};
